import React, { useEffect, useState } from "react";
import { useAppUserDetailsSelector, useAppUserPermissionsSelector } from "src/_common/hooks/selectors/userSelector";
import { hasPermission } from "src/_common/utils/permissionsUtils";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useSubscriptionApi } from "src/_common/hooks/actions/subscription/appSubscriptionApiHook";

interface InvoiceData {
  order_id: string;
  subscription_id: string;
  created_at: string;
  payment_method: string;
  subscription_plan: any;
  amount: number;
  transaction_fee: number;
  status: any;
  card_last4: string;
  invoice_url?: string;
  invoiceFile?: string;
  invoice_number: string;
  tax_id?: string;
  vat_amount?: number;
  usd_subtotal?: number;
  usd_vat_amount?: number;
}

interface InvoicesTabProps {
  invoices?: InvoiceData[];
}

const InvoicesTab: React.FC<InvoicesTabProps> = ({ invoices: propInvoices }) => {
  const userDetails = useAppUserDetailsSelector();
  const userPermissions = useAppUserPermissionsSelector();
  const { t } = useTranslation("settings");
  const { callGetInvoices } = useSubscriptionApi();

  const [invoices, setInvoices] = useState<InvoiceData[]>(propInvoices || []);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;

  // Helper function to check permissions for member users
  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userDetails?.user_type === "member") {
      return hasPermission(userPermissions, menuName);
    }

    // Non-members (employers/admins) have access to all menus
    return true;
  };

  // Check permissions for different actions
  const canReadBillingInvoices = canAccessMenu("read_billing_invoices");

  useEffect(() => {
    loadInvoices(currentPage);
  }, [currentPage]);

  // If member doesn't have read permission, don't render the component
  if (userDetails?.user_type === "member" && !canReadBillingInvoices) {
    return null;
  }

  const loadInvoices = (pages: number) => {
    setLoading(true);
    const page = pages;

    callGetInvoices(
      { per_page: itemsPerPage, page },
      (message: string, data: any) => {
        if (data && Array.isArray(data.invoices)) {
          console.log("Invoices data:", data.invoices);
          setInvoices(data.invoices);
          setTotalCount(data.pagination.total || 0);
          setTotalPages(Math.ceil((data.pagination.total || 0) / itemsPerPage));
        }
        setLoading(false);
      },
      (error: string) => {
        console.error("Failed to load invoices:", error);
        setLoading(false);
      },
    );
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            className={`page-num ${currentPage === i ? "active" : ""}`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>,
        );
      }
    } else {
      // Show first page
      pages.push(
        <button key={1} className={`page-num ${currentPage === 1 ? "active" : ""}`} onClick={() => handlePageChange(1)}>
          1
        </button>,
      );

      // Show dots if current page is far from start
      if (currentPage > 3) {
        pages.push(<span key="dots-start">...</span>);
      }

      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <button
            key={i}
            className={`page-num ${currentPage === i ? "active" : ""}`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>,
        );
      }

      // Show dots if current page is far from end
      if (currentPage < totalPages - 2) {
        pages.push(<span key="dots-end">...</span>);
      }

      // Show last page
      pages.push(
        <button
          key={totalPages}
          className={`page-num ${currentPage === totalPages ? "active" : ""}`}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>,
      );
    }

    return pages;
  };

  const handleDownloadInvoice = (invoiceUrl?: string) => {
    if (invoiceUrl) {
      window.open(invoiceUrl, "_blank", "noopener,noreferrer");
    } else {
      console.warn("No invoice URL available");
    }
  };

  return (
    <div className="billing-tab-content">
      {loading && (
        <div className="billing-loading">
          <i className="fa fa-spinner fa-spin"></i> Loading invoices...
        </div>
      )}

      <div className="billing-table-wrapper">
        <table className="billing-table">
          <thead>
            <tr>
              <th>{t("billing.invoices.table.invoiceId")}</th>
              <th>{t("billing.invoices.table.date")}</th>
              <th>{t("billing.invoices.table.product")}</th>
              <th>{t("billing.invoices.table.transactionAmount")}</th>
              <th>{t("billing.invoices.table.taxAmount")}</th>
              <th>{t("billing.invoices.table.status")}</th>
              <th>{t("billing.invoices.table.method")}</th>
              <th>{t("billing.invoices.table.gateway")}</th>
              <th>{t("billing.invoices.table.invoice")}</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && !loading ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: "20px" }}>
                  No invoices found
                </td>
              </tr>
            ) : (
              invoices.map((invoice, index) => (
                <tr key={`${invoice.order_id}`}>
                  <td className="billing-invoice-id">
                    {invoice.payment_method == "telr"
                      ? invoice.order_id
                      : invoice.payment_method == "paypal"
                        ? invoice.subscription_id
                        : invoice.invoice_number}
                  </td>
                  <td>{invoice.created_at}</td>
                  <td>{invoice.subscription_plan?.title}</td>
                  <td className="billing-amount">
                    {invoice.payment_method === "telr"
                      ? `$ ${invoice?.usd_subtotal ?? 0}`
                      : `$ ${invoice?.amount ?? 0}`}
                  </td>
                  <td className="billing-amount">
                    {invoice.tax_id && invoice.vat_amount ? (
                      <>
                        {invoice.payment_method === "telr"
                          ? `$ ${invoice?.usd_vat_amount ?? 0}`
                          : `$ ${invoice.vat_amount}`}
                      </>
                    ) : (
                      <>
                        {invoice.payment_method === "telr" ? "$" : "$"} {invoice?.transaction_fee ?? 0}
                      </>
                    )}
                  </td>
                  <td>
                    <span className="billing-status-badge billing-status-success">{invoice.status}</span>
                  </td>
                  <td>
                    {invoice.card_last4 ? (
                      <span className="billing-method">
                        <i className="fa fa-credit-card billing-method-icon"></i>
                        {invoice.card_last4}
                      </span>
                    ) : invoice.payment_method == null ? (
                      "FREE"
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    <img
                      style={{ width: "30px" }}
                      src={
                        invoice.payment_method === "telr"
                          ? "/images/visa-icon.png"
                          : invoice.payment_method === "paypal"
                            ? "/images/paypal-icon.png"
                            : "/images/free.png"
                      }
                    />
                  </td>
                  <td>
                    {invoice.payment_method == null ? (
                      "-"
                    ) : (
                      <button
                        className="billing-invoice-btn"
                        title={t("billing.invoices.downloadInvoice")}
                        onClick={() => handleDownloadInvoice(invoice.invoice_url)}
                        disabled={!invoice.invoice_url}
                      >
                        <i className="fa fa-file-text billing-file-icon"></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button className="page-btn" onClick={handlePrevious} disabled={currentPage === 1}>
            {t("billing.invoices.pagination.prev")}
          </button>
          <div className="page-numbers">{renderPageNumbers()}</div>
          <button className="page-btn" onClick={handleNext} disabled={currentPage === totalPages}>
            {t("billing.invoices.pagination.next")}
          </button>
        </div>
      )}
    </div>
  );
};

export default InvoicesTab;
